import React, { createContext, useCallback, useContext, useRef, useState } from "react";

type PageContextType = {
  register: (element: HTMLElement) => () => void;
  getPageInfo: (element: HTMLElement | null) => { pageNumber: number | null, totalPages: number };
};

const PageNumberContext = createContext<PageContextType | null>(null);

export const usePageNumberContext = () => {
  const ctx = useContext(PageNumberContext);
  if (!ctx) throw new Error("PageNumber must be used within PageNumberProvider");
  return ctx;
};

export const PageNumberProvider = ({ children }: { children: React.ReactNode }) => {
  const registeredPages = useRef<HTMLElement[]>([]);
  const [, setRevision] = useState(0);

  const register = useCallback((element: HTMLElement) => {
    if (!registeredPages.current.includes(element)) {
      registeredPages.current.push(element);
      setRevision((current) => current + 1);
    }

    return () => {
      registeredPages.current = registeredPages.current.filter((item) => item !== element);
      setRevision((current) => current + 1);
    };
  }, []);

  const getPageInfo = (element: HTMLElement | null) => {
    const pages = registeredPages.current
      .filter((item) => item.isConnected)
      .sort((a, b) => {
        if (a === b) return 0;
        return a.compareDocumentPosition(b) & 4 ? -1 : 1;
      });

    const index = element ? pages.indexOf(element) : -1;
    return {
      pageNumber: index >= 0 ? index + 1 : null,
      totalPages: pages.length,
    };
  };

  return (
    <PageNumberContext.Provider value={{ register, getPageInfo }}>
      {children}
    </PageNumberContext.Provider>
  );
};
